import React, { useEffect, useState } from 'react';
import {
  getEnvironmentalLens,
  getTraditionalKnowledge,
  getSpatialLens,
  getSeasonalLens,
  getCommunityHealthLens,
} from '../services/lensesService';
import { getTenants } from '../services/tenantService';
import {
  DistributionBar,
  RankedList,
  StatCard,
  LensSection,
  RecordRow,
  MonthHeatmap,
  CommunityRow,
} from '../components/lenses/LensPanel';
import HarvestMap from '../components/harvesting/HarvestMap';

// ─── Constants ────────────────────────────────────────────────────────────────
const LENSES = [
  {
    id:          'community-health',
    icon:        '🏘️',
    label:       'Community Health',
    description: 'Comparative dashboard across communities — membership, engagement, module usage.',
    color:       '#7c3aed',
  },
  {
    id:          'seasonal',
    icon:        '🍂',
    label:       'Seasonal',
    description: 'Harvest calendar, season distributions, year-over-year trends, top species.',
    color:       '#ea580c',
  },
  {
    id:          'spatial',
    icon:        '🗺️',
    label:       'Spatial',
    description: 'Geographic distribution of harvest records across communities.',
    color:       '#0284c7',
  },
  {
    id:          'traditional-knowledge',
    icon:        '🌿',
    label:       'Traditional Knowledge',
    description: 'Traditional-use sites, cultural knowledge submissions, ancestral harvesting areas.',
    color:       '#16a34a',
  },
  {
    id:          'environmental',
    icon:        '🌍',
    label:       'Environmental',
    description: 'Environmental concerns and hazards reported across communities.',
    color:       '#dc2626',
  },
];

const CATEGORY_LABEL = {
  plants_medicine: '🌿 Plants & Medicine',
  hunting:         '🦌 Hunting',
  birds:           '🦅 Birds',
  fishing:         '🎣 Fishing',
  environmental:   '⚠️ Environmental',
  other:           '📍 Other',
};

const SEASON_ICON  = { spring: '🌸', summer: '☀️', fall: '🍂', winter: '❄️' };
const SEASON_COLOR = { spring: '#22c55e', summer: '#f59e0b', fall: '#ea580c', winter: '#60a5fa' };

const S = {
  page:    { padding: '28px 32px', maxWidth: 1140, margin: '0 auto' },
  heading: { fontSize: 24, fontWeight: 800, marginBottom: 6 },
  sub:     { color: '#6b7280', fontSize: 15, marginBottom: 28 },
  err:     {
    background: '#fef2f2', border: '1px solid #fca5a5',
    borderRadius: 6, padding: '10px 14px', color: '#991b1b', fontSize: 14, marginBottom: 14,
  },
  lensCard: (active, color) => ({
    background: active ? color + '10' : '#fff',
    border: active ? `2px solid ${color}` : '1px solid #e5e7eb',
    borderRadius: 10, padding: '14px 18px', cursor: 'pointer',
    display: 'flex', alignItems: 'flex-start', gap: 12,
    transition: 'all .15s',
  }),
  filterRow: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' },
  select: {
    padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: 6,
    fontSize: 13, background: '#fff', cursor: 'pointer',
  },
};

// ─── Individual lens panels ───────────────────────────────────────────────────

function EnvironmentalPanel({ tenantId }) {
  const [data, setData]    = useState(null);
  const [loading, setLoad] = useState(true);
  const [error, setError]  = useState('');

  useEffect(() => {
    setLoad(true); setError('');
    getEnvironmentalLens(tenantId ? { tenantId } : {})
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.message || 'Failed to load'))
      .finally(() => setLoad(false));
  }, [tenantId]);

  if (loading) return <p>Loading environmental lens…</p>;
  if (error)   return <div style={S.err}>{error}</div>;
  if (!data)   return null;

  const totalRecords  = data.statusBreakdown?.reduce((a, b) => a + b.count, 0) || 0;

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
        <StatCard value={totalRecords}              label="Total Concerns"          color="#dc2626" />
        <StatCard value={data.byTenant?.length || 0} label="Communities Reporting" color="#7c3aed" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        <LensSection title="By Concern Type">
          <RankedList
            items={data.bySubcategory}
            labelKey="_id"
            countKey="count"
            colorFn={() => '#dc2626'}
            emptyMsg="No subcategory data yet."
          />
        </LensSection>
        <LensSection title="By Community">
          <RankedList
            items={data.byTenant}
            labelKey="tenantName"
            countKey="count"
            colorFn={() => '#7c3aed'}
            emptyMsg="No community data yet."
          />
        </LensSection>
      </div>

      <LensSection title="Recent Environmental Reports">
        {!data.recent?.length
          ? <p style={{ color: '#6b7280', fontSize: 13 }}>No recent reports.</p>
          : data.recent.map((r) => (
              <RecordRow
                key={r._id}
                icon="⚠️"
                primary={r.subcategory || r.species || 'Unknown concern'}
                secondary={r.locationDescription || r.observations?.slice(0, 80) || ''}
                meta={`${r.tenantId?.name || 'Unknown community'} · ${new Date(r.createdAt || r.harvestDate).toLocaleDateString()} · by ${r.userId?.name || '—'}`}
              />
            ))
        }
      </LensSection>
    </div>
  );
}

function TraditionalKnowledgePanel({ tenantId }) {
  const [data, setData]    = useState(null);
  const [loading, setLoad] = useState(true);
  const [error, setError]  = useState('');

  useEffect(() => {
    setLoad(true); setError('');
    getTraditionalKnowledge(tenantId ? { tenantId } : {})
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.message || 'Failed to load'))
      .finally(() => setLoad(false));
  }, [tenantId]);

  if (loading) return <p>Loading traditional knowledge lens…</p>;
  if (error)   return <div style={S.err}>{error}</div>;
  if (!data)   return null;

  const totalSites = data.byTenant?.reduce((a, b) => a + b.sites, 0) || 0;

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
        <StatCard value={totalSites}                      label="Traditional Use Sites"  color="#16a34a" />
        <StatCard value={data.recentNotes?.length || 0}  label="Knowledge Notes"         color="#0284c7" />
        <StatCard value={data.byTenant?.length || 0}     label="Communities"             color="#7c3aed" />
      </div>

      {data.sites?.some((r) => r.location?.coordinates?.length === 2) && (
        <LensSection title="Traditional Use Sites Map">
          <HarvestMap records={data.sites} readonly />
        </LensSection>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        <LensSection title="Sites by Category">
          <RankedList
            items={data.byCategory}
            labelKey="_id"
            countKey="count"
            colorFn={() => '#16a34a'}
            emptyMsg="No category data yet."
          />
        </LensSection>
        <LensSection title="Sites by Community">
          {data.byTenant?.map((t) => (
            <DistributionBar
              key={String(t._id)}
              label={t.tenantName || 'Unknown'}
              count={t.sites}
              max={Math.max(...(data.byTenant.map((x) => x.sites)), 1)}
              color="#16a34a"
            />
          ))}
        </LensSection>
      </div>

      <LensSection title="Recent Knowledge Submissions">
        {!data.recentNotes?.length
          ? <p style={{ color: '#6b7280', fontSize: 13 }}>No knowledge notes yet.</p>
          : data.recentNotes.map((r) => (
              <RecordRow
                key={r._id}
                icon="📜"
                primary={r.species || CATEGORY_LABEL[r.category] || 'Unnamed'}
                secondary={r.traditionalKnowledge?.slice(0, 120)}
                meta={`${r.tenantId?.name || '—'} · ${new Date(r.createdAt).toLocaleDateString()} · by ${r.userId?.name || '—'}`}
              />
            ))
        }
      </LensSection>
    </div>
  );
}

function SpatialPanel({ tenantId }) {
  const [data, setData]    = useState(null);
  const [loading, setLoad] = useState(true);
  const [error, setError]  = useState('');

  useEffect(() => {
    setLoad(true); setError('');
    getSpatialLens(tenantId ? { tenantId } : {})
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.message || 'Failed to load'))
      .finally(() => setLoad(false));
  }, [tenantId]);

  if (loading) return <p>Loading spatial lens…</p>;
  if (error)   return <div style={S.err}>{error}</div>;
  if (!data)   return null;

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <StatCard value={data.total} label="Geo-tagged Records" color="#0284c7" />
      </div>
      {data.records?.length > 0
        ? (
          <LensSection title={`${data.total} locations${tenantId ? '' : ' across all communities'}`}>
            <HarvestMap records={data.records} readonly />
          </LensSection>
        )
        : <p style={{ color: '#6b7280' }}>No geo-tagged records yet. Records need latitude/longitude to appear here.</p>
      }
    </div>
  );
}

function SeasonalPanel({ tenantId }) {
  const [data, setData]    = useState(null);
  const [loading, setLoad] = useState(true);
  const [error, setError]  = useState('');

  useEffect(() => {
    setLoad(true); setError('');
    getSeasonalLens(tenantId ? { tenantId } : {})
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.message || 'Failed to load'))
      .finally(() => setLoad(false));
  }, [tenantId]);

  if (loading) return <p>Loading seasonal lens…</p>;
  if (error)   return <div style={S.err}>{error}</div>;
  if (!data)   return null;

  const totalRecords = data.bySeason?.reduce((a, b) => a + b.count, 0) || 0;

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
        {data.bySeason?.map((s) => (
          <StatCard
            key={s._id}
            value={s.count}
            label={`${SEASON_ICON[s._id] || ''} ${s._id}`}
            sub={`${totalRecords > 0 ? Math.round((s.count / totalRecords) * 100) : 0}% of total`}
            color={SEASON_COLOR[s._id] || '#6b7280'}
          />
        ))}
      </div>

      <LensSection title="Month of Year Activity">
        <MonthHeatmap byMonth={data.byMonthOfYear} color="#16a34a" />
        <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 8 }}>
          Darker cells = more harvest records that month across all years.
        </p>
      </LensSection>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        <LensSection title="Season × Category">
          {['spring', 'summer', 'fall', 'winter'].map((season) => {
            const rows = data.bySeasonAndCategory?.filter((r) => r._id.season === season) || [];
            if (!rows.length) return null;
            const seasonMax = Math.max(...rows.map((r) => r.count), 1);
            return (
              <div key={season} style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: SEASON_COLOR[season], marginBottom: 6 }}>
                  {SEASON_ICON[season]} {season.charAt(0).toUpperCase() + season.slice(1)}
                </div>
                {rows.map((r) => (
                  <DistributionBar
                    key={r._id.category}
                    label={CATEGORY_LABEL[r._id.category] || r._id.category}
                    count={r.count}
                    max={seasonMax}
                    color={SEASON_COLOR[season]}
                  />
                ))}
              </div>
            );
          })}
        </LensSection>

        <LensSection title="Top Species / Resources">
          {!data.topSpecies?.length
            ? <p style={{ color: '#6b7280', fontSize: 13 }}>No species data yet.</p>
            : data.topSpecies.map((s, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex', justifyContent: 'space-between',
                    padding: '6px 0', borderBottom: '1px solid #f3f4f6', fontSize: 14,
                  }}
                >
                  <span style={{ color: '#374151' }}>{s._id}</span>
                  <span style={{ fontWeight: 700, color: '#374151' }}>{s.count}</span>
                </div>
              ))
          }
        </LensSection>
      </div>
    </div>
  );
}

function CommunityHealthPanel() {
  const [data, setData]    = useState(null);
  const [loading, setLoad] = useState(true);
  const [error, setError]  = useState('');

  useEffect(() => {
    setLoad(true); setError('');
    getCommunityHealthLens()
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.message || 'Failed to load'))
      .finally(() => setLoad(false));
  }, []);

  if (loading) return <p>Loading community health lens…</p>;
  if (error)   return <div style={S.err}>{error}</div>;
  if (!data)   return null;

  const communities   = data.communities || [];
  const maxHarvest    = Math.max(...communities.map((c) => c.harvest.total), 1);
  const maxMembers    = Math.max(...communities.map((c) => c.members), 1);
  const totalMembers  = communities.reduce((a, c) => a + c.members, 0);
  const totalHarvest  = communities.reduce((a, c) => a + c.harvest.total, 0);
  const totalResponses = communities.reduce((a, c) => a + c.consultation.responses, 0);

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
        <StatCard value={communities.length} label="Active Communities"       color="#7c3aed" />
        <StatCard value={totalMembers}       label="Total Members"             color="#0284c7" />
        <StatCard value={totalHarvest}       label="Harvest Records"           color="#16a34a" />
        <StatCard value={totalResponses}     label="Consultation Responses"    color="#ea580c" />
      </div>
      <p>Harvest data is contributor-reported, not staff-approved. Records with quality flags are excluded from these lens results; absence of a flag does not verify accuracy.</p>

      <LensSection title="Community Profiles">
        {communities.length === 0
          ? <p style={{ color: '#6b7280' }}>No communities found.</p>
          : communities.map((c) => (
              <CommunityRow
                key={String(c._id)}
                community={c}
                maxHarvest={maxHarvest}
                maxMembers={maxMembers}
              />
            ))
        }
      </LensSection>
    </div>
  );
}

// ─── Lens/panel dispatch map ──────────────────────────────────────────────────
const PANEL_MAP = {
  'environmental':         EnvironmentalPanel,
  'traditional-knowledge': TraditionalKnowledgePanel,
  'spatial':               SpatialPanel,
  'seasonal':              SeasonalPanel,
  'community-health':      CommunityHealthPanel,
};

// ─── LensesPage ──────────────────────────────────────────────────────────────
const LensesPage = () => {
  const [activeLens, setActiveLens]         = useState('community-health');
  const [tenants, setTenants]               = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('');

  useEffect(() => {
    getTenants()
      .then((r) => setTenants(r.data?.tenants || r.data || []))
      .catch(() => {});
  }, []);

  const ActivePanel    = PANEL_MAP[activeLens];
  const lensConfig     = LENSES.find((l) => l.id === activeLens);
  const supportsFilter = activeLens !== 'community-health';

  return (
    <div style={S.page}>
      <h1 style={S.heading}>🔭 Lenses</h1>
      <p style={S.sub}>
        Cross-community intelligence synthesis — apply analytical perspectives to harvesting, consultation, and community data.
      </p>

      {/* ── Lens selector grid ── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: 10, marginBottom: 28,
        }}
      >
        {LENSES.map((lens) => (
          <div
            key={lens.id}
            style={S.lensCard(activeLens === lens.id, lens.color)}
            onClick={() => setActiveLens(lens.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && setActiveLens(lens.id)}
          >
            <span style={{ fontSize: 26, flexShrink: 0 }}>{lens.icon}</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: activeLens === lens.id ? lens.color : '#1f2937' }}>
                {lens.label}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2, lineHeight: 1.4 }}>
                {lens.description}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Optional tenant filter ── */}
      {supportsFilter && tenants.length > 1 && (
        <div style={S.filterRow}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
            {lensConfig?.icon} {lensConfig?.label} —
          </span>
          <select
            value={selectedTenant}
            onChange={(e) => setSelectedTenant(e.target.value)}
            style={S.select}
          >
            <option value="">All communities</option>
            {tenants.map((t) => (
              <option key={t._id} value={t._id}>
                {t.branding?.displayName || t.name}
              </option>
            ))}
          </select>
          {selectedTenant && (
            <button
              onClick={() => setSelectedTenant('')}
              style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 13 }}
            >
              ✕ Clear
            </button>
          )}
        </div>
      )}

      {/* ── Active lens result panel ── */}
      <div
        style={{
          background: '#fff',
          border: `1px solid ${lensConfig?.color || '#e5e7eb'}`,
          borderTop: `4px solid ${lensConfig?.color || '#e5e7eb'}`,
          borderRadius: 10,
          padding: '24px 28px',
        }}
      >
        {ActivePanel && (
          <ActivePanel tenantId={supportsFilter ? selectedTenant : undefined} />
        )}
      </div>
    </div>
  );
};

export default LensesPage;
